import { startTransition, useEffect, useReducer, useRef } from 'react';
/**
 * Subscribe to a SPECIFIC SET of records by id and return the ones that exist
 * (overlay-aware), in `ids` order. Re-renders ONLY when one of those ids
 * changes — NOT on every collection mutation.
 *
 * Use this instead of `useLiveQuery`/`getAll().filter` when a component needs a
 * known handful of records (e.g. a task card resolving its subtask ids). The
 * `getAll().filter` pattern makes EVERY such component a whole-collection
 * subscriber: one edit anywhere wakes all of them and each does an O(n) scan
 * (N components × O(n) = O(N·n) of synchronous recompute on a single edit).
 * This hook keeps each component's cost O(ids).
 *
 * The returned array is reference-stable while the resolved records are
 * unchanged (so it's safe as a dependency / memo input). Re-renders triggered
 * by changes are wrapped in `startTransition` so they never block the
 * interaction that caused them.
 */ export function useCollectionItems(collection, ids) {
    const [, bump] = useReducer((n)=>n + 1, 0);
    // Stable primitive key so the effect re-subscribes only when the id SET
    // changes, not on every render (the caller usually builds `ids` inline).
    const idsKey = ids.join(' ');
    const resultRef = useRef([]);
    const idsRef = useRef(ids);
    idsRef.current = ids;
    const compute = ()=>{
        const out = [];
        for (const id of idsRef.current){
            const value = collection.get(id);
            if (value !== undefined) out.push(value);
        }
        return out;
    };
    const sameRefs = (a, b)=>{
        if (a.length !== b.length) return false;
        for(let i = 0; i < a.length; i += 1){
            if (!Object.is(a[i], b[i])) return false;
        }
        return true;
    };
    // Initialize once (and whenever the id set changes) so the first render has
    // the current values.
    const initialized = useRef('');
    if (initialized.current !== idsKey) {
        resultRef.current = compute();
        initialized.current = idsKey;
    }
    useEffect(()=>{
        // Capture changes since the last render, then subscribe per id.
        const next = compute();
        if (!sameRefs(resultRef.current, next)) {
            resultRef.current = next;
            bump();
        }
        const onChange = ()=>{
            const recomputed = compute();
            if (!sameRefs(resultRef.current, recomputed)) {
                resultRef.current = recomputed;
                startTransition(()=>bump());
            }
        };
        const unsubs = idsRef.current.map((id)=>collection.subscribeToId(id, onChange));
        return ()=>unsubs.forEach((u)=>u());
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        collection,
        idsKey
    ]);
    return resultRef.current;
}
